/**
 * @file Makes customElements.define() idempotent. SE-1004.
 *
 * Importing this module patches the global registry so that defining a tag
 * name that is already registered becomes a no-op instead of throwing.
 *
 * Why this is needed
 * ------------------
 * The critical bundle (src/critical.ts) registers the above-the-fold
 * components, and pulls in their dependencies -- se-container, outline-icon,
 * se-heading and friends -- as a side effect. Those same dependencies are also
 * reachable from the lazily loaded component modules. When outline-loader later
 * imports, say, se-landing-hero, that module's graph runs
 * `customElements.define('se-heading', ...)` a second time.
 *
 * Natively that throws NotSupportedError, and because the throw happens during
 * module evaluation it takes down the *whole* import -- so se-landing-hero
 * never registers and its content silently fails to render. The loader's own
 * `customElements.get()` check cannot prevent this: it guards the component it
 * was asked to load, not the transitive @customElement side effects inside it.
 *
 * The first definition wins. Both copies are built from the same source, so
 * which one wins does not matter -- only that every <se-heading> on the page
 * resolves to a single class.
 *
 * Import this before anything that defines a custom element.
 */

const KEY = '__seDefineOncePatched';

interface PatchedRegistry extends CustomElementRegistry {
  [KEY]?: boolean;
}

const registry = customElements as PatchedRegistry;

if (!registry[KEY]) {
  const nativeDefine = customElements.define.bind(customElements);

  customElements.define = function defineOnce(
    name: string,
    constructor: CustomElementConstructor,
    options?: ElementDefinitionOptions
  ): void {
    if (customElements.get(name)) {
      // Already registered -- almost always the critical bundle having got
      // there first. Skip rather than throwing and killing the import.
      return;
    }
    nativeDefine(name, constructor, options);
  };

  registry[KEY] = true;
}
